import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const card = {
  background: "#1a1a2e",
  border: "1px solid #2a2a4a",
  borderRadius: "10px",
  padding: "20px",
};

const btn = (bg, color, disabled) => ({
  padding: "9px 16px",
  background: disabled ? "#2a2a4a" : bg,
  color: disabled ? "#555" : color,
  border: "none",
  borderRadius: "5px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: "13px",
  fontWeight: "bold",
});

const WorkoutEntry = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [validationError, setValidationError] = useState("");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    if (authgear.sessionState !== "AUTHENTICATED") {
      navigate("/login");
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    setJsonInput(e.target.value);
    setValidationError("");
    setFileName("");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      setValidationError("Please upload a .json file.");
      return;
    }
    setFileName(file.name);
    setValidationError("");
    const reader = new FileReader();
    reader.onload = (event) => {
      setJsonInput(event.target.result);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const validateJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Basic validation for required fields
      if (!parsed.plan_name) {
        throw new Error("Missing required field: plan_name");
      }
      if (!parsed.description) {
        throw new Error("Missing required field: description");
      }
      if (!parsed.plan_data) {
        throw new Error("Missing required field: plan_data");
      }

      return parsed;
    } catch (error) {
      throw new Error(`Invalid JSON: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationError("");

    try {
      const workoutPlan = validateJson(jsonInput);

      await authgear.refreshAccessTokenIfNeeded();
      const accessToken = authgear.accessToken;

      const endpoint = updateExisting ? "/api/workouts/upsert" : "/api/workouts";
      const method = updateExisting ? "put" : "post";

      const { data } = await axios({
        method,
        url: base_url + endpoint,
        data: workoutPlan,
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      console.log("API Response:", data);
      setLoading(false);
      if (data.success) {
        const message = data.workoutsUpdated !== undefined
          ? `Workout plan saved! ${data.workoutsCreated} created, ${data.workoutsUpdated} updated.`
          : `Workout plan saved! ${data.workoutsCreated} workout(s) created.`;
        alert(message);
        setJsonInput("");
      } else {
        console.log("Save failed, response:", data);
        alert(`Failed to save workout plan: ${data.message || "Please try again."}`);
      }
    } catch (error) {
      setLoading(false);
      if (error.message.includes("Invalid JSON")) {
        setValidationError(error.message);
      } else {
        console.error("Error saving workout plan:", error);
        alert("Error saving workout plan. Please try again.");
      }
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
      <Header />
      <div style={{ padding: "60px 20px 40px", maxWidth: "800px", margin: "0 auto" }}>
        <h2 style={{ color: "#00d4ff", margin: "0 0 6px" }}>Create Workout Plan</h2>
        <p style={{ color: "#888", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
          Paste your workout plan JSON below, or upload a <code>.json</code> file. Claude can also edit
          individual days directly through the hooks-data MCP server.
        </p>

        <form onSubmit={handleSubmit} style={{ ...card, display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <label htmlFor="jsonInput" style={{ fontSize: "14px", color: "#aaa" }}>
                Workout Plan JSON
              </label>
              <label style={{
                display: "inline-block",
                cursor: "pointer",
                background: "#111128",
                border: "1px solid #2a2a4a",
                borderRadius: "5px",
                padding: "4px 12px",
                fontSize: "12px",
                color: "#ccc",
                userSelect: "none"
              }}>
                {fileName ? `File: ${fileName}` : "Upload .json file"}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
              </label>
            </div>
            <textarea
              id="jsonInput"
              name="jsonInput"
              value={jsonInput}
              placeholder="Paste your workout plan JSON here..."
              onChange={handleInputChange}
              rows="15"
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: "12px",
                padding: "10px",
                background: "#111128",
                color: "#ddd",
                border: validationError ? "1px solid #f87171" : "1px solid #2a2a4a",
                borderRadius: "5px",
                resize: "vertical"
              }}
              required
            />
          </div>

          {validationError && (
            <div style={{
              color: "#f88",
              background: "#2a1a1a",
              border: "1px solid #4a2a2a",
              padding: "10px",
              borderRadius: "5px",
              fontSize: "13px"
            }}>
              <strong>Validation Error:</strong> {validationError}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="updateExisting"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#00d4ff" }}
            />
            <label htmlFor="updateExisting" style={{ fontSize: "13px", color: "#aaa", cursor: "pointer" }}>
              Update existing workouts (replaces workouts with matching dates)
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !jsonInput.trim()}
            style={btn("#00d4ff", "#0d0d1a", loading || !jsonInput.trim())}
          >
            {loading ? "Saving..." : "Save Workout Plan"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WorkoutEntry;
